/* Happy Little Doers — upsell surfaces (vanilla ES6+, no dependencies).
   Two independent pieces, both guarded so repeat section includes never double-bind:

     1. PDP "Add accessories" picker — keeps the running grand total in step with the
        ticked add-ons and the quantity stepper. The actual multi-item add lives in
        <hld-quick-add> (hld-theme.js), which reads the ticked boxes at submit time.

     2. <hld-quick-view> — a shared modal that previews any product in place, filled
        from sections/hld-quick-view.liquid via the Section Rendering API.

   The cart-drawer upsell needs no JS at all: it is server-rendered inside the drawer's
   [data-hld-cart-refresh] region, so every cart change re-renders it with the
   already-in-cart filter and prices recomputed in Liquid. */
(function () {
  'use strict';

  /* Same <html> lock the drawer, search overlay and mobile menu share — never
     `overflow: hidden` on body, which would break the sticky header (see the note at
     the top of hld-theme.js). */
  const setScrollLock = (on) => document.documentElement.toggleAttribute('scroll-lock', !!on);
  const otherOverlayOpen = () =>
    !!document.querySelector('.hld-drawer.is-open, .hld-navbar__menu.is-open, .hld-searchov.is-open');

  const emit = (name, detail) =>
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));

  /* ---- 1. PDP "Add accessories" running total --------------------------- */
  if (!window.__hldUpsellTotals) {
    window.__hldUpsellTotals = true;

    const formatMoney = (cents, block) => {
      const currency = block.getAttribute('data-currency') || 'GBP';
      const locale = block.getAttribute('data-locale') || 'en-GB';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    };

    /* The stepper lives in the sibling {% form %}, not inside the block, so reach for
       it through the shared <hld-quick-add> host. */
    const quantityFor = (block) => {
      const host = block.closest('hld-quick-add') || block.parentElement;
      const input = host && host.querySelector('input[name="quantity"]');
      const n = input ? parseInt(input.value, 10) : 1;
      return n > 0 ? n : 1;
    };

    const sync = (block) => {
      const total = block.querySelector('[data-hld-upsell-total]');
      const value = block.querySelector('[data-hld-upsell-total-value]');
      if (!total || !value) return;

      const checked = [...block.querySelectorAll('[data-hld-upsell-check]')].filter((c) => c.checked);
      checked.forEach((c) => c.closest('.hld-upsell__row').classList.add('is-picked'));
      [...block.querySelectorAll('[data-hld-upsell-check]:not(:checked)')].forEach((c) => {
        const row = c.closest('.hld-upsell__row');
        if (row) row.classList.remove('is-picked');
      });

      /* Nothing ticked → the normal product price above is already the true total, so
         showing a second identical number would only add noise. */
      if (!checked.length) {
        total.hidden = true;
        return;
      }

      const base = parseInt(block.getAttribute('data-base-price'), 10) || 0;
      const addOns = checked.reduce((sum, c) => sum + (parseInt(c.getAttribute('data-price'), 10) || 0), 0);
      value.textContent = formatMoney(base * quantityFor(block) + addOns, block);
      total.hidden = false;
    };

    const syncAll = () => document.querySelectorAll('[data-hld-upsell]').forEach(sync);

    document.addEventListener('change', (e) => {
      const check = e.target.closest('[data-hld-upsell-check]');
      if (check) {
        const block = check.closest('[data-hld-upsell]');
        /* A multi-variant add-on can't be ticked blind — we'd be guessing which SKU.
           Bounce it into Quick View so the shopper chooses, and leave the box clear. */
        if (check.checked && check.hasAttribute('data-multi')) {
          check.checked = false;
          const url = check.getAttribute('data-product-url');
          const modal = document.getElementById('hld-quick-view');
          if (url && modal && modal.openFor) modal.openFor(url, check);
          if (block) sync(block);
          return;
        }
        if (block) sync(block);
        emit('hld:upsell:select', { variantId: check.getAttribute('data-variant-id'), checked: check.checked });
        return;
      }
      if (e.target.closest('input[name="quantity"]')) syncAll();
    });

    /* The stepper buttons in hld-pdp.js already fire a bubbling `change`, but a typed
       quantity only fires `input` until blur — catch that too so the total never lags. */
    document.addEventListener('input', (e) => {
      if (e.target.closest('input[name="quantity"]')) syncAll();
    });

    /* <hld-quick-add> clears the ticks after a successful multi-add. */
    document.addEventListener('hld:upsell:sync', syncAll);

    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', syncAll)
      : syncAll();
  }

  /* ---- 1b. Analytics ----------------------------------------------------
     The point of all three surfaces is AOV, which is unfalsifiable without knowing
     which surface earned each add. Shopify already fires its own GA4 `add_to_cart`,
     so these are deliberately custom event names — duplicating the standard one would
     double-count every add and quietly corrupt the store's ecommerce reporting.

     Consume in GTM with a Custom Event trigger on hld_upsell_*, or read them off
     window.dataLayer directly. */
  if (!window.__hldUpsellAnalytics) {
    window.__hldUpsellAnalytics = true;

    const push = (event, payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, payload));
    };

    const surfaceOf = (el) => {
      if (el.closest('#hld-cart-drawer')) return 'cart-drawer';
      if (el.closest('.hld-upsell--page')) return 'cart-page';
      if (el.closest('.hld-atf__purchase')) return 'pdp';
      return 'other';
    };

    const rowData = (row) => ({
      item_name: row.querySelector('.hld-upsell__title')?.textContent.trim(),
      variant_id: row.querySelector('[data-hld-upsell-check]')?.getAttribute('data-variant-id') || null,
    });

    /* Impressions. Deduped per surface+item for the life of the page: a shopper
       scrolling the offer in and out of view hasn't seen a new offer, and counting it
       twice would deflate the conversion rate we're trying to read. */
    const seen = new Set();
    const io =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((e) => {
                if (!e.isIntersecting) return;
                io.unobserve(e.target);
                const surface = surfaceOf(e.target);
                const data = rowData(e.target);
                const key = surface + '|' + (data.variant_id || data.item_name);
                if (seen.has(key)) return;
                seen.add(key);
                push('hld_upsell_view', Object.assign({ surface }, data));
              });
            },
            { threshold: 0.5 }
          )
        : null;

    const observeRows = (root) => {
      if (!io) return;
      (root || document).querySelectorAll('.hld-upsell__row').forEach((row) => io.observe(row));
    };

    document.addEventListener('hld:upsell:select', (e) => {
      const check = document.querySelector(`[data-hld-upsell-check][data-variant-id="${e.detail.variantId}"]`);
      push('hld_upsell_select', {
        surface: check ? surfaceOf(check) : 'pdp',
        variant_id: e.detail.variantId,
        checked: e.detail.checked,
        item_name: check ? rowData(check.closest('.hld-upsell__row')).item_name : null,
      });
    });

    document.addEventListener('hld:upsell:quickview', (e) => push('hld_upsell_quickview', { url: e.detail.url }));

    document.addEventListener('hld:cart:added', (e) => {
      const d = e.detail;
      if (!d) return; // the Horizon cart:update bridge re-emits without detail
      push('hld_upsell_add', {
        surface: d.surface,
        upsell_count: d.upsellCount,
        line_count: d.items ? d.items.length : 1,
      });
    });

    /* The drawer and cart page replace [data-hld-cart-refresh] wholesale on every cart
       change, so rows observed at load are detached and never fire again. Re-observe
       whatever the refresh put there. */
    const watchRefreshRegions = () => {
      document.querySelectorAll('[data-hld-cart-refresh]').forEach((region) => {
        if (region.__hldObserved) return;
        region.__hldObserved = true;
        new MutationObserver(() => observeRows(region)).observe(region, { childList: true, subtree: true });
      });
    };

    const start = () => { observeRows(); watchRefreshRegions(); };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  }

  /* ---- 2. <hld-quick-view> : in-place product preview -------------------- */
  if (!customElements.get('hld-quick-view')) {
    customElements.define('hld-quick-view', class extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.hld-qvmodal__panel');
        this.target = this.querySelector('[data-hld-qv-target]');
        this.loadingMarkup = this.target ? this.target.innerHTML : '';
        this.sectionId = this.getAttribute('data-section-id') || 'hld-quick-view';
        this.cache = new Map();
        this.open = false;

        /* Delegated: upsell rows are re-rendered by the cart refresh, so triggers come
           and go. Binding per-element would silently stop working after the first
           cart change. */
        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-hld-qv-open]');
          if (trigger) {
            const url = trigger.getAttribute('data-product-url');
            if (url) { e.preventDefault(); this.openFor(url, trigger); }
            return;
          }
          if (e.target.closest('[data-hld-qv-close]')) { e.preventDefault(); this.hide(); }
        });

        /* Warm the cache before the click lands. A hover or a tab-to is a strong
           intent signal and buys us the whole network round-trip, which is what keeps
           the visible open well inside the 500ms budget on a cold cache. */
        const warm = (e) => {
          const trigger = e.target.closest && e.target.closest('[data-hld-qv-open]');
          if (trigger) this.prefetch(trigger.getAttribute('data-product-url'));
        };
        document.addEventListener('pointerenter', warm, true);
        document.addEventListener('focusin', warm);

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.open) { e.preventDefault(); this.hide(); }
        });
        this.addEventListener('keydown', (e) => { if (e.key === 'Tab') this.trapFocus(e); });

        /* Adding from inside the modal hands over to the cart drawer, which slides in
           on hld:cart:added. Two stacked overlays reads as a mistake, so step aside. */
        document.addEventListener('hld:cart:added', () => { if (this.open) this.hide(); });

        /* Keep the headline price honest when the shopper switches variant. */
        this.addEventListener('change', (e) => {
          const select = e.target.closest('[data-hld-qv-variant]');
          if (!select) return;
          const price = this.querySelector('[data-hld-qv-price]');
          const opt = select.options[select.selectedIndex];
          if (price && opt && opt.dataset.price) price.textContent = opt.dataset.price;
        });
      }

      /* ---- data ---- */
      fetchUrl(productUrl) {
        const join = productUrl.indexOf('?') === -1 ? '?' : '&';
        return productUrl + join + 'section_id=' + encodeURIComponent(this.sectionId);
      }

      prefetch(productUrl) {
        if (!productUrl || !window.fetch || this.cache.has(productUrl)) return this.cache.get(productUrl);
        const p = fetch(this.fetchUrl(productUrl), { headers: { Accept: 'text/html' } })
          .then((res) => {
            if (!res.ok) throw new Error('quick view fetch failed');
            return res.text();
          })
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const fresh = doc.querySelector('.hld-qv');
            if (!fresh) throw new Error('quick view markup missing');
            return fresh.outerHTML;
          })
          .catch((err) => {
            /* Never cache a failure — the next hover should get a clean retry. */
            this.cache.delete(productUrl);
            throw err;
          });
        this.cache.set(productUrl, p);
        return p;
      }

      /* ---- open / close ---- */
      async openFor(productUrl, trigger) {
        if (!productUrl || !window.fetch) {
          if (productUrl) window.location.href = productUrl;
          return;
        }
        const token = (this.token = (this.token || 0) + 1);
        this.show(trigger);

        if (this.target) {
          this.target.setAttribute('aria-busy', 'true');
          if (!this.cache.has(productUrl)) this.target.innerHTML = this.loadingMarkup;
        }

        try {
          const html = await this.prefetch(productUrl);
          /* A second trigger may have been clicked while this was in flight; only the
             latest open is allowed to paint. */
          if (token !== this.token || !this.open || !this.target) return;
          this.target.innerHTML = html;
          this.target.setAttribute('aria-busy', 'false');
          const focusable = this.target.querySelector('select, button, a[href]');
          if (focusable) focusable.focus({ preventScroll: true });
          emit('hld:upsell:quickview', { url: productUrl });
        } catch (err) {
          if (token !== this.token) return;
          /* The product page is the source of truth — send them there rather than
             leaving a dead modal on screen. */
          this.hide();
          window.location.href = productUrl;
        }
      }

      show(trigger) {
        if (this.open) return;
        this.opener = trigger || null;
        this.scrollY = window.scrollY || window.pageYOffset || 0;

        this.hidden = false;
        /* Read layout so the transition has a start state before the class flips.
           Deliberately not requestAnimationFrame — rAF is throttled in background
           tabs, which would strand the panel invisible. Same call as openDrawer(). */
        if (this.panel) void this.panel.offsetWidth;
        this.classList.add('is-open');
        this.open = true;
        setScrollLock(true);

        const close = this.querySelector('[data-hld-qv-close]');
        /* preventScroll: the panel is position: fixed, so a plain focus() scrolls the
           document to the top to "reveal" it and the shopper loses their place. */
        if (close) close.focus({ preventScroll: true });
      }

      hide() {
        if (!this.open) return;
        this.classList.remove('is-open');
        this.open = false;
        this.token = (this.token || 0) + 1; // cancel any in-flight paint

        /* The drawer, mobile menu and search overlay share this one <html> lock —
           unlocking here while one of them is open would leave the page scrolling
           behind its own backdrop. */
        if (!otherOverlayOpen()) setScrollLock(false);

        if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
        if (typeof this.scrollY === 'number' && Math.abs(window.scrollY - this.scrollY) > 1) {
          /* `instant` because the theme sets scroll-behavior: smooth globally, and a
             restore that visibly glides is worse than the jump it fixes. */
          window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
        }

        setTimeout(() => {
          if (this.open) return;
          this.hidden = true;
          if (this.target) this.target.innerHTML = this.loadingMarkup;
        }, 260);
      }

      trapFocus(e) {
        const focusables = [...this.querySelectorAll('a[href], button:not([disabled]), select, input:not([type="hidden"])')]
          .filter((el) => !el.hidden && el.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }
})();
